import { AdBeaconType } from './AdMetadata';
import { Error } from '@quickplay/foundation';

/* =========================================================
 * MediaTailor Ad – Core Domain Model
 * ======================================================= */
export interface DatazoomAdMetaData {
  adSessionId?: string;
  adMetadata?: DatazoomMediaTailorAdsResponse;
  isFallback?: boolean;
  fallbackReason?: Error;
  manifestUrl?: string;
}
export interface DatazoomMediaTailorAd {
  /* Identity */
  adId: string;
  vastAdId?: string;
  creativeId?: string;
  creativeSequence?: string;

  /* Metadata */
  adTitle?: string;
  adSystem?: string;
  adParameters?: string;

  /* Timing */
  start: number; // seconds
  duration: number; // seconds
  startDate?: number;
  skipOffset?: number;

  /* Avail (Ad Break) */
  availId: string;
  availStart: number;
  availDuration: number;
  availMarkedDuration?: number | null;
  availAdCount: number;
  availAdIndex: number;
  inAvailStartOffset?: number;

  /* Playback */
  playCount?: number;

  /* Tracking */
  trackingEvents?: MediaTailorTrackingEvent[];
  adBreakTrackingEvents?: MediaTailorTrackingEvent[];
  quartileEventOffsets?: number[];

  /* Click & companions */
  videoClickThrough?: string;
  companionAds?: MediaTailorCompanionAd[];

  /* Icons & OMID verification */
  icons?: MediaTailorIcon[];
  adVerifications?: MediaTailorAdVerification[];

  /* VAST Extensions */
  extensions?: MediaTailorExtension[];
}

/* =========================================================
 * Tracking
 * ======================================================= */

export interface MediaTailorTrackingEvent {
  eventType: AdBeaconType;
  eventId: string;
  beaconUrls: string[];
  startTimeInSeconds: number;
  durationInSeconds: number;
}

/* =========================================================
 * OMID / Ad Verification (VAST 4.x)
 * ======================================================= */

export interface MediaTailorAdVerification {
  vendor?: string;
  executableResources?: ExecutableResource[];
  javaScriptResources?: JavaScriptResource[];
  trackingEvents?: VerificationTrackingEvent[];
  verificationParameters?: string;
}

export interface ExecutableResource {
  apiFramework?: string;
  uri?: string;
}

export interface JavaScriptResource {
  apiFramework?: string;
  browserOptional?: 'true' | 'false';
  uri?: string;
}

export interface VerificationTrackingEvent {
  event?: string;
  uri?: string;
}

/* =========================================================
 * Companion Ads
 * ======================================================= */

export interface MediaTailorCompanionAd {
  id?: string;
  width?: number;
  height?: number;
  resource?: string;
  clickThroughUrl?: string;
}

/* =========================================================
 * Icons
 * ======================================================= */

export interface MediaTailorIcon {
  program?: string;
  width?: number;
  height?: number;
  xPosition?: number;
  yPosition?: number;
  duration?: number;
  resource?: string;
  clickThroughUrl?: string;
}

/* =========================================================
 * VAST Extensions
 * ======================================================= */

export interface MediaTailorExtension {
  type?: string;
  value?: unknown;
}

/* =========================================================
 * Playback / UI State
 * ======================================================= */

export interface AdCountdown {
  /** Seconds remaining until current ad ends */
  toAdEnd: number;

  /** Seconds remaining until entire ad avail ends */
  toAvailEnd: number;

  /** Seconds until ad becomes skippable (null = not skippable) */
  toSkipThreshold: number | null;
}

export interface AdObject {
  adClickthroughUrl: string | null;
  adData: DatazoomMediaTailorAd;
  targetPlayheadForAdSkip: number;
}

export interface AdPlaybackState {
  adCountdown: AdCountdown;
  adElapsedTime: number;
  adObject: AdObject;
}

/**
 * Defines how MediaTailor ad tracking updates are requested from the server.
 *
 * FULL       - Requests tracking information for all ads in the current manifest window.
 * PLAYHEAD   - Requests tracking information only for ad avails near the current
 *              playhead position. Requires AWS MediaTailor support to enable.
 * PAGINATION - Requests only new tracking information using a pagination token,
 *              avoiding retransmission of previously received data. (Default)
 */
export enum AdTrackingUpdateMode {
  FULL = 'full',
  PLAYHEAD = 'playhead',
  PAGINATION = 'pagination',
}
/**
 * Enum representing the Open Measurement (OMID) access mode for ad viewability and measurement.
 *
 * OMID defines how much access the measurement SDK has to the video content, ad creative, and reporting.
 */
export enum OmidAccessMode {
  /**
   * FULL - Full access allowed for the OMID SDK.
   * The SDK can measure the entire ad experience including video content and ad creative.
   * Use this mode when you want complete measurement for viewability, quartiles, and verification.
   */
  FULL = 'full',

  /**
   * CREATIVE - Creative-only measurement access.
   * The SDK can only measure the ad creative itself, not the surrounding content.
   * Use this mode when you want to limit measurement strictly to the ad creative.
   */
  CREATIVE = 'creative',

  /**
   * DOMAIN - Domain-limited measurement access.
   * The SDK is restricted to the publisher's domain context for measurement.
   * Use this mode when measurement should remain confined to the publisher’s site or app domain.
   */
  DOMAIN = 'domain',

  /**
   * LIMITED - Highly restricted measurement access.
   * Minimal data is collected; only essential viewability or verification metrics are tracked.
   * Use this mode for privacy-first scenarios or when user consent is limited.
   */
  LIMITED = 'limited',
}

/* =========================================================
 * API Response
 * ======================================================= */

export type DatazoomMediaTailorAdsResponse = DatazoomMediaTailorAd[];
